import type { CallCategory } from "@expert/shared-types";

import { getLogger } from "@expert/logging";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const logger = getLogger({ module: "callCategoryStore" });
const STORAGE_KEY = "pacing:callCategory";

function persistCategory(sessionId: string | undefined, category: CallCategory) {
    if (!sessionId) return;
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, category }));
    } catch (error) {
        logger.warn({ error, sessionId, category }, "Failed to persist call category to sessionStorage");
    }
}

function clearPersistedCategory() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        logger.warn({ error }, "Failed to clear persisted call category from sessionStorage");
    }
}

export function loadPersistedCategory(sessionId: string): CallCategory | undefined {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as { sessionId: string; category: CallCategory };
        // Only restore if it belongs to the current session
        if (parsed.sessionId === sessionId) return parsed.category;
        // Stale entry from a previous session — clean it up
        clearPersistedCategory();
    } catch (error) {
        logger.warn({ error, sessionId }, "Failed to load persisted call category from sessionStorage");
        clearPersistedCategory();
    }
    return undefined;
}

interface CallCategoryStore {
    callCategory: CallCategory | undefined;
    sessionId: string | undefined;
    setCallCategory: (category: CallCategory) => void;
    hydrate: (sessionId: string) => void;
    reset: () => void;
}

export const useCallCategoryStore = create<CallCategoryStore>()(
    devtools(
        (set, get) => ({
            callCategory: undefined,
            sessionId: undefined,
            setCallCategory: (category) => {
                const { sessionId } = get();
                persistCategory(sessionId, category);
                set({ callCategory: category }, false, "setCallCategory");
            },
            hydrate: (sessionId) => {
                const restored = loadPersistedCategory(sessionId);
                set({ sessionId, callCategory: restored }, false, "hydrate");
            },
            reset: () => {
                clearPersistedCategory();
                set({ callCategory: undefined, sessionId: undefined }, false, "reset");
            },
        }),
        {
            enabled: import.meta.env.VITE_ENV_TYPE !== "production",
            name: "CallCategoryStore",
            store: "callCategory",
        },
    ),
);
